import { useMemo, useState, useEffect } from "react";
import type { TranscriptionSegment } from "@core/features/video";
import VideoMeetingSubtitleItem from "./VideoMeetingSubtitleItem";

const DISPLAY_DURATION = 3000;

type Props = {
  segments: TranscriptionSegment[];
  currentSegment: TranscriptionSegment | null;
  isEnabled: boolean;
  isLoading?: boolean;
  error?: string | null;
  attendeeCount?: number;
};

export function VideoMeetingSubtitles({
  segments,
  currentSegment,
  isEnabled,
  isLoading,
  error,
  attendeeCount = 2,
}: Props) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isEnabled) return;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 100);
    return () => clearInterval(interval);
  }, [isEnabled]);

  const recentFinalSegments = useMemo(() => {
    if (!isEnabled) return [];

    return segments
      .filter(
        (seg) =>
          !seg.isPartial &&
          now - seg.startTime <= DISPLAY_DURATION &&
          seg.text.trim().length > 0
      )
      .sort((a, b) => b.startTime - a.startTime)
      .slice(0, 3);
  }, [segments, now, isEnabled]);

  const livePartialSegment =
    currentSegment?.isPartial && currentSegment.text.length > 0
      ? currentSegment
      : null;

  const hasContent =
    isEnabled && (recentFinalSegments.length > 0 || !!livePartialSegment);

  if (!isEnabled) return null;

  if (attendeeCount <= 1) return null;

  if (isLoading) {
    return (
      <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
        <div className="flex flex-col gap-4">
          <div className="bg-black/80 text-white px-4 py-2 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Starting transcription...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
        <div className="flex flex-col gap-4">
          <div className="bg-red-600/80 text-white px-4 py-2 rounded-lg">
            <span>Error: {error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!hasContent) return null;

  return (
    <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
      <div className="flex flex-col gap-4">
        {recentFinalSegments
          .slice()
          .reverse()
          .map((segment) => (
            <VideoMeetingSubtitleItem
              key={segment.id}
              speaker={segment.speakerName}
              text={segment.text}
            />
          ))}
        {livePartialSegment && (
          <VideoMeetingSubtitleItem
            key={livePartialSegment.id}
            speaker={livePartialSegment.speakerName}
            text={livePartialSegment.text}
          />
        )}
      </div>
    </div>
  );
}
